import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

@Component({
  selector: 'app-editable-textarea-with-links',
  templateUrl: './editable-textarea-with-links.component.html',
  styleUrls: ['./editable-textarea-with-links.component.scss']
})
export class EditableTextareaWithLinksComponent implements OnInit {
  @Input() textValue = '';
  @Input() maxNumberOfCharacters = 100;
  @Input() placeholderText?: string;
  @Input() isHighlighted = false;
  @Input() disabled = false;
  @Output() valueChange = new EventEmitter<string>();

  @ViewChild('editor', { static: true }) editorRef: ElementRef<HTMLElement>;

  numberOfCharacters = 0;

  private static readonly URL_PATTERN = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/ig;

  ngOnInit(): void {
    this.updateCharacterCount(this.textValue || '');
    this.editorRef.nativeElement.innerHTML = this.convertTextToHTML(this.textValue || '');
  }

  private updateCharacterCount(value: string): void {
    this.numberOfCharacters = value.length;
  }

  onInput(event: InputEvent): void {
    const element = this.editorRef.nativeElement;
    let newValue = element.innerText;
    const caret = this.getCaretOffset(element);

    const tooLong = newValue.length > this.maxNumberOfCharacters;
    if (tooLong) {
      newValue = newValue.slice(0, this.maxNumberOfCharacters);
    }

    if (tooLong || this.linksChanged(element, newValue)) {
      element.innerHTML = this.convertTextToHTML(newValue);
      this.setCaretOffset(element, Math.min(caret, newValue.length));
    }

    this.updateCharacterCount(newValue);
    this.valueChange.emit(newValue);
  }

  onKeyDown(event: KeyboardEvent): void {
    const element = event.target as HTMLElement;
    const currentLength = element.innerText.length;
    if (currentLength >= this.maxNumberOfCharacters && event.key !== 'Backspace' && event.key !== 'Delete') {
      event.preventDefault();
    }
  }

  private convertTextToHTML(text: string): string {
    const escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return escaped.replace(EditableTextareaWithLinksComponent.URL_PATTERN,
      '<a href="$1" target="_blank" rel="noopener noreferrer" class="link" contenteditable="false">$1</a>');
  }

  private linksChanged(element: HTMLElement, text: string): boolean {
    const urls = text.match(EditableTextareaWithLinksComponent.URL_PATTERN) || [];
    const links = Array.from(element.querySelectorAll('a')).map(a => a.textContent);
    return urls.length !== links.length || urls.some((url, i) => url !== links[i]);
  }

  private getCaretOffset(element: HTMLElement): number {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return element.innerText.length;
    }
    const range = selection.getRangeAt(0);
    if (range.endContainer === element) {
      return this.childLength(element, range.endOffset);
    }
    let offset = 0;
    let found = false;
    this.walk(element, (node, length) => {
      if (found) {
        return;
      }
      if (node === range.endContainer) {
        offset += node.nodeType === Node.TEXT_NODE ? range.endOffset : this.childLength(node, range.endOffset);
        found = true;
        return;
      }
      offset += length;
    });
    return found ? offset : element.innerText.length;
  }

  private setCaretOffset(element: HTMLElement, offset: number): void {
    const selection = window.getSelection();
    if (!selection) {
      return;
    }
    const range = document.createRange();
    let remaining = offset;
    let placed = false;
    this.walk(element, (node, length) => {
      if (placed) {
        return;
      }
      if (node.nodeType === Node.TEXT_NODE && remaining <= length) {
        range.setStart(node, remaining);
        placed = true;
        return;
      }
      remaining -= length;
    });
    if (!placed) {
      range.selectNodeContents(element);
      range.collapse(false);
    } else {
      range.collapse(true);
    }
    selection.removeAllRanges();
    selection.addRange(range);
  }

  private walk(root: Node, visit: (node: Node, length: number) => void): void {
    root.childNodes.forEach(child => {
      if (child.nodeType === Node.TEXT_NODE) {
        visit(child, (child.textContent || '').length);
      } else if (child.nodeName === 'BR') {
        visit(child, 1);
      } else if (child.nodeName === 'A') {
        visit(child, (child.textContent || '').length);
      } else {
        if ((child.nodeName === 'DIV' || child.nodeName === 'P') && child.previousSibling) {
          visit(document.createTextNode(''), 1);
        }
        visit(child, 0);
        this.walk(child, visit);
      }
    });
  }

  private childLength(node: Node, index: number): number {
    let length = 0;
    for (let i = 0; i < index && i < node.childNodes.length; i++) {
      const child = node.childNodes[i];
      length += child.nodeName === 'BR' ? 1 : (child.textContent || '').length;
    }
    return length;
  }
}
